import { useDispatch } from 'react-redux';
import {
  DataGrid,
  GridPagination,
  GridRowParams,
  GridSortModel,
  gridPageCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';

import { ReactComponent as AscendingSortingIcon } from '../assets/icons/main/ascending_sorting.svg';
import { ReactComponent as DescendingSortingIcon } from '../assets/icons/main/descending_sorting.svg';
import { ReactComponent as NoSortIcon } from '../assets/icons/main/no-sort.svg';


import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TablePaginationProps,
  TextField,
  Typography,
  lighten,
  useTheme
} from '@mui/material';
import CheckboxComponent from './Checkbox';
import { get, isArray, isBoolean, isEmpty, isString, pad } from 'lodash';
import MuiPagination from '@mui/material/Pagination';
import { useOutletContext } from 'react-router-dom';

let CURRENT_PAGE = 0;

interface PaginationPropsTypes extends Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'> {
  totalRowCount: number;
  pageNumber: number;
  pageSize: number;
  isServerPagination?: string;
  onPagination: (pageSize: number, pageNumber: number) => void;
}

function Pagination({
  page,
  onPageChange,
  className,
  totalRowCount,
  pageNumber = 1,
  pageSize,
  isServerPagination,
  onPagination,
}: PaginationPropsTypes) {
  const apiRef = useGridApiContext();
  const clientPageCount = useGridSelector(apiRef, gridPageCountSelector);
  const serverPageCount = Math.ceil(totalRowCount / pageSize);

  CURRENT_PAGE = isServerPagination ? pageNumber : page + 1;

  return (
    <MuiPagination
      color='primary'
      variant='outlined'
      shape='rounded'
      className={className}
      count={isServerPagination ? serverPageCount : clientPageCount}
      page={CURRENT_PAGE}
      sx={{
        '& .MuiPagination-ul .MuiButtonBase-root': {
          minWidth: '34px',
          height: '34px',
          width: '34px',
          '& svg': {
            width: '34px',
            height: '34px',
          },
        },
      }}
      onChange={(event, newPage) => {
        onPageChange(event as any, newPage - 1);

        if (isServerPagination) {
          onPagination(pageSize, newPage);
        }
      }}
    />
  );
}

const DataTable = ({
  columns = [],
  rows = [],
  pageSizeOptions = [20],
  disableColumnMenu = true,
  checkboxSelection,
  disableRowSelectionOnClick,
  autoHeight,
  loading,
  hideFooter,
  showColumnFilters,
  onRowSelectionChange,
  tableHeight,
  hideBorders,
  autoPageSize,
  noRowsOverlay = () => (
    <Stack height='100%' alignItems='center' justifyContent='center'>
      No rows
    </Stack>
  ),
  onRowClick = () => { },
  pageSize = 10,
  otherProps = {},
  hiddenColumns,
  divstyle,
  selectionModel,
  isRowSelectable,
  isServerPagination,
  totalRowCount,
  pageNumber,
  onPagination = () => { },
  onSorting = () => { },
  tableStyles = {},
}: any) => {
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', '');
  const styles = {
    root: {
      '& .MuiDataGrid-cell:focus-within': {
        outline: 'none',
      },
      '&.MuiDataGrid-root': {
        border: 'none',
        fontSize: '1rem',
        minHeight: tableHeight ? 'calc(100vh - 560px)' : 'calc(100vh - 278px)',
        backgroundColor: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05),
        borderBottomRightRadius: '18px',
        borderBottomLeftRadius: '18px',
        borderTopRightRadius: '0px',
        borderTopLeftRadius: '0px',
      },
      '.MuiDataGrid-columnHeaderTitle': {
        textTransform: 'uppercase',
        color: themeData.palette.text.primary, //'#434D67',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: 'normal',
      },
      '& .MuiDataGrid-withBorderColor': {
        borderColor: hideBorders ? 'transparent !important' : `${themeData.palette.customBorderColor.main}`,  //'rgba(224, 224, 224, 1)',
      },
      '& .MuiDataGrid-row--lastVisible': {
        '& .MuiDataGrid-withBorderColor': {
          borderColor: (rows.length === 10) ? 'transparent !important' : `${themeData.palette.customBorderColor.main}`,
        },
      },
      '& .MuiDataGrid-row:nth-of-type(odd)': {
        backgroundColor: 'rgba(248, 250, 251, 0.5)',
        border: hideBorders //? 'transparent' :`1px solid ${darken(theme.palette.background.default, 0)}` ,//'1px solid rgba(217, 223, 237, 0.5)',
      },
      '.MuiDataGrid-iconButtonContainer': {
        visibility: 'visible',
      },
      '.MuiDataGrid-sortIcon': {
        opacity: 'inherit !important',
      },
      // '.MuiDataGrid-row.data-table-row-odd': {
      //   backgroundColor: '#fbfcfc',
      // },
      '.MuiDataGrid-virtualScroller': {
        overflow: 'auto',
        overflowY: 'hidden',
        minHeight: tableHeight ? 'calc(100vh - 500px)' : 'calc(100vh - 362px)',
        // minHeight:'calc(100vh - 178px)',
        marginLeft: '-2px',
        paddingBottom: hideFooter ? '32px' : '0'
      },
      '.MuiDataGrid-cellContent': {
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: '400',
        color: themeData.palette.text.secondary, //'#64759F',
        pointerEvents: 'none',
      },
      '.MuiDataGrid-cell': {
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: '400',
        color: themeData.palette.text.secondary, //'#64759F',

      },
      '.MuiDataGrid-columnHeader:first-of-type': {
        paddingLeft: checkboxSelection ? '6px' : '24px',
        marginRight: checkboxSelection ? '10px' : '0px',
      },
      '&.MuiDataGrid-root .MuiDataGrid-row:nth-of-type(odd)': {
        backgroundColor: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.2) : lighten(themeData.palette.background.default, 0.08),
        ':hover': {
          filter: 'brightness(98%)'
        },
      },
      '&.MuiDataGrid-root .MuiDataGrid-row:nth-of-type(even)': {
        backgroundColor: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05),
        ':hover': {
          filter: 'brightness(98%)'
        }
      },
      '.MuiDataGrid-columnHeader:last-child': {
        paddingRight: 3,
      },
      '.MuiDataGrid-cell:first-of-type': {
        paddingLeft: checkboxSelection ? '33px' : '26px',
        paddingRight: checkboxSelection ? '29px' : '10px',
      },
      '.MuiDataGrid-cell:nth-last-of-type(2)': {
        paddingRight: 3,
      },
      '.MuiDataGrid-columnHeaderCheckbox': {
        //  marginRight: '15px',
        paddingTop: '0px',
      },
      '.MuiDataGrid-cellCheckbox': {
        width: '24px',
        height: '24px',
      },
    },
  };

  const SortedAscendingIcon = () => {
    return <AscendingSortingIcon style={{ height: '13px', width: '13px', color: themeData.palette.custom.light }} />
  };

  const SortedDescendingIcon = () => {
    return <DescendingSortingIcon style={{ height: '13px', width: '13px', color: themeData.palette.custom.light }} />
  };

  const UnsortedIcon = () => {
    return <NoSortIcon style={{ height: '13px', width: '13px', color: themeData.palette.custom.light }} />
  };

  const [filterValues, setFilterValues] = useState<any>({});
  const dispatch = useDispatch();

  const filteredRows = useMemo(() => {
    return rows.filter((row: any) => {
      return Object.keys(filterValues ?? {}).every((filterKey) => {
        const filterValue = filterValues[filterKey];
        if (!isBoolean(filterValue) && isEmpty(filterValue)) {
          return true;
        } else if (isString(filterValue)) {
          if (filterValue !== 'all') {
            return row[filterKey].includes(filterValue.toLowerCase());
          } else {
            return true;
          }
        } else if (isArray(filterValue)) {
          return row[filterKey].some((item: any) => filterValue.includes(item.name));
        } else {
          return row[filterKey] === filterValue;
        }
      });
    });
  }, [rows, filterValues]);

  const filterRow = useMemo(() => {
    if (showColumnFilters) {
      let row: Record<string, any> = { id: 0 };
      for (let column of columns) {
        row[column.field] = '';
      }
      return row;
    }
    return {};
  }, [showColumnFilters]);

  const columnsWithFilters = columns.map((col: any) => {
    return {
      ...col,
      renderCell: (params: any) => {
        if (params.id) {
          return col.renderCell(params) || params.value;
        } else if (col.customFilterReq) {
          return (
            <div>
              <TextField
                value={filterValues[params.field] || ''}
                onChange={(e: any) => {
                  setFilterValues((pre: any) => ({
                    ...pre,
                    [params.field]: e.target.value,
                  }));
                }}
              />
            </div>
          );
        } else if (col.customDropdownFilterReq) {
          const isMultiSelect = get(col, 'isMultiSelect', false);
          return (
            <Select
              key={col.field}
              value={filterValues[params.field] ?? []}
              multiple={isMultiSelect}
              onChange={(e: any) => {
                setFilterValues((pre: any) => ({
                  ...pre,
                  [params.field]: e.target.value,
                }));
              }}
              renderValue={(selected: any) => {
                if (isMultiSelect) {
                  return selected.join(', ');
                } else {
                  const item = get(col, 'options', []).find((option: any) => option.value === selected);
                  return get(item, 'label', '');
                }
              }}
              color='secondary'
              style={{ width: '100%', margin: 0 }}
            >
              {(col.options || []).map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                  {get(col, 'isMultiSelect', false) ? (
                    <Checkbox checked={(filterValues[params.field] || []).includes(option.label)} />
                  ) : null}
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          );
        }
        return '';
      },
    };
  });

  return (
    <div style={divstyle}>
      <DataGrid
        rows={rows.length > 0 ? (showColumnFilters ? [filterRow, ...filteredRows] : rows) : rows}
        columns={showColumnFilters ? columnsWithFilters : columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: pageSize },
          },
        }}
        pagination
        pageSizeOptions={pageSizeOptions}
        disableColumnMenu={disableColumnMenu}
        sx={[styles.root, tableStyles]}
        checkboxSelection={checkboxSelection}
        columnVisibilityModel={hiddenColumns}
        slots={{
          columnSortedAscendingIcon: SortedAscendingIcon,
          columnSortedDescendingIcon: SortedDescendingIcon,
          columnUnsortedIcon: UnsortedIcon,
          noRowsOverlay: noRowsOverlay,
          footer: (props: any) => {
            const startIndex = (CURRENT_PAGE - 1) * pageSize + 1;
            const endIndex = Math.min(CURRENT_PAGE * pageSize, isServerPagination ? totalRowCount : rows.length);
            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: `1px solid ${themeData.palette.customBorderColor.main}`,
                }}
              >
                <Box>
                  <Typography
                    sx={{ padding: '12px 12px 12px 28px', fontSize: '0.9rem', fontWeight: 400, color: themeData.palette.text.primary }}
                  >
                    {startIndex < 0
                      ? `Loading...`
                      : `Showing ${startIndex}-${endIndex} of ${isServerPagination ? totalRowCount : rows.length
                      } entries`}
                  </Typography>
                </Box>
                <Box sx={{ padding: '8px 22px 8px 12px' }}>
                  <GridPagination
                    sx={{ '.MuiTablePagination-displayedRows': { display: 'none' } }}
                    ActionsComponent={(props: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) => (
                      <Pagination
                        {...props}
                        pageNumber={pageNumber}
                        totalRowCount={totalRowCount}
                        pageSize={pageSize}
                        isServerPagination={isServerPagination}
                        onPagination={onPagination}
                      />
                    )}
                    {...props}
                  />
                </Box>
              </Box>
            );
          },
          baseCheckbox: (props: any) => {
            return (
              <CheckboxComponent
                disabled={props.disabled}
                handleCheckboxChange={props.onChange}
                checked={props.checked}
                style={{
                  ml: 1
                }}
              />
            );
          },
        }}

        autoHeight={autoHeight}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        loading={loading}
        rowHeight={70}
        getRowClassName={(params: any) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'data-table-row-even' : 'data-table-row-odd'
        }
        hideFooter={hideFooter}
        autoPageSize={autoPageSize}
        isRowSelectable={(params: GridRowParams) => (checkboxSelection ? isRowSelectable(params) : '')}
        onRowSelectionModelChange={(params: any) => onRowSelectionChange(params)}
        rowSelectionModel={selectionModel}
        onRowClick={onRowClick}
        paginationMode={isServerPagination ? 'server' : 'client'}
        rowCount={totalRowCount}
        sortingMode={isServerPagination ? 'server' : 'client'}
        onSortModelChange={(sortModel: GridSortModel) => {
          onSorting(sortModel.length ? sortModel[0] : { field: '', sort: '' });
        }}
        {...otherProps}
      />
    </div>
  );
};

export default DataTable;
